/**
 * Other Strategies (gifting, funeral bonds, annuity) for Retirement Village manual control.
 * Mirrors Aged Care Other Strategies; funding sources use post–village-funding simulated factFind.
 */
import React, { useMemo } from 'react';
import { ChevronDown, Info, ShieldCheck } from 'lucide-react';
import { Card, FormCheckbox, FormSelect, CurrencyInput } from '../../components/ui/index.tsx';
import { calculateDeprivedAssets } from '../../../../shared/src/calculation/giftingDeprivation.ts';
import { buildAnnuityImpact, summariseAnnuityImpact } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import {
    GIFTING_LIMIT_ANNUAL,
    GIFTING_LIMIT_5_YEAR,
    FUNERAL_BOND_LIMIT,
} from '../../constants/index.ts';
import { ASSET_TYPES } from '../../../../shared/src/calculation/constants/assetTypes.ts';
import SupportAtHomeAnnuityConfigurator, { DEFAULT_NEW_ANNUITY_RESIDUAL } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import type { AnnuityDetails, AnnuityFundingSource } from '../supportAtHome/SupportAtHomeAnnuityConfigurator.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind, FactFindGiftsHistory } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { OtherStrategies } from './RetirementVillagePage.tsx';

interface FundingOption {
    value: string;
    label: string;
}

function buildLiquidFundingOptions(
    liquidFactFind: DraftFactFind | null | undefined,
    factFind: DraftFactFind,
    otherStrategies: OtherStrategies,
    entryFeeAllocations: Record<string, number | string> = {},
): FundingOption[] {
    const options: FundingOption[] = [];
    const liquidAssetTypes = ASSET_TYPES.filter((t) => t.isLiquid).map((t) => t.id);
    liquidAssetTypes.forEach((type) => {
        (liquidFactFind?.assets?.[type] || []).forEach((asset) => {
            const assetId = `${type}-${asset.id}`;
            const assetValue = asset.value || asset.balance || 0;
            const entryAlloc = parseFloat(String(entryFeeAllocations[assetId] || 0)) || 0;
            let otherAlloc = 0;
            if (otherStrategies.gifting && otherStrategies.giftingSource === assetId) {
                otherAlloc += otherStrategies.giftingAmount || 0;
            }
            if (otherStrategies.funeralBonds && otherStrategies.funeralBondSource === assetId) {
                otherAlloc += otherStrategies.funeralBondAmount || 0;
            }
            const available = Math.max(0, assetValue - entryAlloc - otherAlloc);
            if (available <= 0) return;
            const ownership = asset.ownership || 'client';
            const ownershipLabel =
                ownership === 'joint'
                    ? 'Joint'
                    : ownership === 'partner'
                      ? factFind?.partner?.firstName || 'Partner'
                      : factFind?.client?.firstName || 'Client';
            const assetName =
                asset.description || (asset as { name?: string }).name || ASSET_TYPES.find((at) => at.id === type)?.name || type;
            options.push({
                value: assetId,
                label: `${assetName} (${ownershipLabel}) — ${formatCurrency(available)} available`,
            });
        });
    });
    if (options.length === 0) {
        options.push({ value: 'bankAccounts', label: 'Bank accounts / cash' });
    }
    return options;
}

export interface RVOtherStrategiesSectionProps {
    factFind: DraftFactFind;
    liquidFactFindAfterVillage: DraftFactFind | null | undefined;
    entryFeeAllocations: Record<string, number | string>;
    otherStrategies: OtherStrategies;
    setOtherStrategies: React.Dispatch<React.SetStateAction<OtherStrategies>>;
    annuityRecord: AnnuityDetails | null | undefined;
    setAnnuityRecord: React.Dispatch<React.SetStateAction<AnnuityDetails | null>>;
    showOtherStrategies: boolean;
    setShowOtherStrategies: React.Dispatch<React.SetStateAction<boolean>>;
}

const RVOtherStrategiesSection = ({
    factFind,
    liquidFactFindAfterVillage,
    entryFeeAllocations,
    otherStrategies,
    setOtherStrategies,
    annuityRecord,
    setAnnuityRecord,
    showOtherStrategies,
    setShowOtherStrategies,
}: RVOtherStrategiesSectionProps) => {
    const hasFuneralBondsInProfile =
        (factFind.assets?.funeralBonds?.length ?? 0) > 0 &&
        factFind.assets!.funeralBonds!.some((fb) => (parseFloat(String(fb.value ?? 0)) || 0) > 0);

    const fundingOptions = useMemo(
        () => buildLiquidFundingOptions(liquidFactFindAfterVillage, factFind, otherStrategies, entryFeeAllocations),
        [liquidFactFindAfterVillage, factFind, otherStrategies, entryFeeAllocations],
    );

    const annuityFundingSources: AnnuityFundingSource[] = useMemo(() => {
        const sources: AnnuityFundingSource[] = [];
        const liquidAssetTypes = ASSET_TYPES.filter((t) => t.isLiquid).map((t) => t.id);
        liquidAssetTypes.forEach((assetType) => {
            (liquidFactFindAfterVillage?.assets?.[assetType] || []).forEach((asset) => {
                const assetId = `${assetType}-${asset.id}`;
                const entryAlloc = parseFloat(String(entryFeeAllocations[assetId] || 0)) || 0;
                const assetValue = asset.value || asset.balance || 0;
                let otherAlloc = 0;
                if (otherStrategies.gifting && otherStrategies.giftingSource === assetId) {
                    otherAlloc += otherStrategies.giftingAmount || 0;
                }
                if (otherStrategies.funeralBonds && otherStrategies.funeralBondSource === assetId) {
                    otherAlloc += otherStrategies.funeralBondAmount || 0;
                }
                const available = Math.max(0, assetValue - entryAlloc - otherAlloc);
                if (available <= 0) return;
                const ownership = asset.ownership || 'client';
                const ownershipLabel =
                    ownership === 'joint'
                        ? 'Joint'
                        : ownership === 'partner'
                          ? factFind?.partner?.firstName || 'Partner'
                          : factFind?.client?.firstName || 'Client';
                const assetName =
                    asset.description || ASSET_TYPES.find((at) => at.id === assetType)?.name || assetType;
                const mappedType = assetType === 'bankAccounts' ? 'bank' : assetType;
                sources.push({
                    id: assetId,
                    type: mappedType,
                    assetId: asset.id,
                    label: `${assetName} (${ownershipLabel}) — ${formatCurrency(available)} available`,
                    ownership,
                    available,
                });
            });
        });
        if (sources.length === 0) {
            sources.push({
                id: 'bank-default',
                type: 'bank',
                assetId: null,
                label: 'Bank accounts / cash',
                ownership: factFind?.adviceFor === 'couple' ? 'joint' : 'client',
                available: 0,
            });
        }
        return sources;
    }, [liquidFactFindAfterVillage, factFind, otherStrategies, entryFeeAllocations]);

    const annuitySummary = useMemo(() => {
        if (!annuityRecord?.purchaseAmount) return null;
        try {
            const built = buildAnnuityImpact(factFind as unknown as FactFind, annuityRecord);
            return built && !built.warnings ? summariseAnnuityImpact(built) : null;
        } catch {
            return null;
        }
    }, [factFind, annuityRecord]);

    return (
        <Card>
            <button
                type="button"
                onClick={() => setShowOtherStrategies(!showOtherStrategies)}
                className="w-full flex items-center justify-between text-left"
            >
                <h3 className="text-xl font-bold text-gray-800">Other Strategies</h3>
                <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform ${showOtherStrategies ? 'rotate-180' : ''}`}
                />
            </button>

            {showOtherStrategies && (
                <div className="mt-4 space-y-4">
                    <p className="text-sm text-gray-600 mb-4">
                        Optional strategies to reduce ongoing costs and boost Age Pension. These work alongside your
                        entry-funding strategy above.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormCheckbox
                            label="Gifting to Family"
                            name="gifting"
                            checked={otherStrategies.gifting}
                            onChange={(n, v) => setOtherStrategies((s) => ({ ...s, [n]: v }))}
                        />
                        {!hasFuneralBondsInProfile && (
                            <FormCheckbox
                                label="Funeral Bonds"
                                name="funeralBonds"
                                checked={otherStrategies.funeralBonds}
                                onChange={(n, v) => setOtherStrategies((s) => ({ ...s, [n]: v }))}
                            />
                        )}
                    </div>

                    <FormCheckbox
                        label="Lifetime Annuity"
                        name="annuity"
                        checked={otherStrategies.annuity}
                        onChange={(n, v) => {
                            setOtherStrategies((s) => ({ ...s, [n]: v }));
                            if (v && !annuityRecord) {
                                setAnnuityRecord({
                                    annuityType: 'lifetime',
                                    purchaseAmount: 0,
                                    owner: 'client',
                                    // Capital preserved by default — see DEFAULT_NEW_ANNUITY_RESIDUAL.
                                    residualCapital: DEFAULT_NEW_ANNUITY_RESIDUAL,
                                    indexation: false,
                                    reversionary: false,
                                });
                            }
                            if (!v) setAnnuityRecord(null);
                        }}
                    />

                    {otherStrategies.gifting && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                            <h4 className="font-semibold">Gifting Strategy Details</h4>
                            {(() => {
                                const giftsHistory = factFind.giftsHistory || {};
                                if (!giftsHistory.hasGifted) return null;
                                let totalGifted = 0;
                                Object.keys(giftsHistory).forEach((key) => {
                                    if (key.startsWith('year')) {
                                        totalGifted += parseFloat(String(giftsHistory[key] ?? 0)) || 0;
                                    }
                                });
                                const asOf = new Date();
                                const historicalDeprived = calculateDeprivedAssets(giftsHistory as unknown as FactFindGiftsHistory, {}, asOf);
                                const planAmt = otherStrategies.giftingAmount || 0;
                                const provisionalDeprived =
                                    planAmt > 0
                                        ? calculateDeprivedAssets(
                                              giftsHistory as unknown as FactFindGiftsHistory,
                                              { otherStrategies: { gifting: true, giftingAmount: planAmt } },
                                              asOf,
                                          )
                                        : historicalDeprived;
                                const incrementalDeprived = Math.max(0, provisionalDeprived - historicalDeprived);
                                return (
                                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                        <div className="flex items-start gap-3">
                                            <Info className="w-4 h-4 text-blue-600 mt-1 shrink-0" />
                                            <div className="text-sm text-gray-700 space-y-1">
                                                <p className="font-semibold text-gray-900">Previous gifting detected</p>
                                                <p>
                                                    Fact-find gifting: <strong>{formatCurrency(totalGifted)}</strong>
                                                </p>
                                                <p>
                                                    Free areas: {formatCurrency(GIFTING_LIMIT_ANNUAL)} per FY,{' '}
                                                    {formatCurrency(GIFTING_LIMIT_5_YEAR)} over any rolling five FYs.
                                                </p>
                                                {incrementalDeprived > 0 && planAmt > 0 && (
                                                    <p className="font-medium pt-2 border-t border-blue-200">
                                                        This plan adds about{' '}
                                                        <strong>{formatCurrency(incrementalDeprived)}</strong> deprived
                                                        assets.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            <CurrencyInput
                                label="Gift amount ($)"
                                value={otherStrategies.giftingAmount || 0}
                                onChange={(v) =>
                                    setOtherStrategies((s) => ({ ...s, giftingAmount: Math.max(0, Number(v) || 0) }))
                                }
                            />
                            <FormSelect
                                label="Fund from"
                                name="giftingSource"
                                value={otherStrategies.giftingSource || fundingOptions[0]?.value || ''}
                                onChange={(n, v) => setOtherStrategies((s) => ({ ...s, [n]: v }))}
                            >
                                {fundingOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FormSelect>
                        </div>
                    )}

                    {otherStrategies.funeralBonds && !hasFuneralBondsInProfile && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                            <h4 className="font-semibold">Funeral Bonds Strategy Details</h4>
                            <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200/50">
                                <div className="flex items-start gap-3">
                                    <ShieldCheck className="w-4 h-4 text-teal-600 mt-1 shrink-0" />
                                    <p className="text-sm text-gray-700">
                                        Funeral bonds are exempt from the asset test (up to{' '}
                                        {formatCurrency(FUNERAL_BOND_LIMIT)} per person), which can help boost Age
                                        Pension.
                                    </p>
                                </div>
                            </div>
                            <CurrencyInput
                                label="Funeral bond amount ($)"
                                value={otherStrategies.funeralBondAmount || 0}
                                onChange={(v) =>
                                    setOtherStrategies((s) => ({
                                        ...s,
                                        funeralBondAmount: Math.max(0, Number(v) || 0),
                                    }))
                                }
                            />
                            <FormSelect
                                label="Fund from"
                                name="funeralBondSource"
                                value={otherStrategies.funeralBondSource || fundingOptions[0]?.value || ''}
                                onChange={(n, v) => setOtherStrategies((s) => ({ ...s, [n]: v }))}
                            >
                                {fundingOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </FormSelect>
                        </div>
                    )}

                    {otherStrategies.annuity && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
                            <h4 className="font-semibold">Lifetime Annuity</h4>
                            {annuityRecord ? (
                                <SupportAtHomeAnnuityConfigurator
                                    factFind={liquidFactFindAfterVillage || factFind}
                                    annuityDetails={annuityRecord}
                                    setAnnuityDetails={setAnnuityRecord as unknown as (updater: AnnuityDetails | ((prev: AnnuityDetails) => AnnuityDetails)) => void}
                                    annuitySummary={annuitySummary}
                                    annuityWarnings={[]}
                                    fundingSources={annuityFundingSources}
                                    formatCurrency={formatCurrency}
                                    clientAge={factFind?.client?.dob ? calculateAge(factFind.client.dob) : null}
                                    partnerAge={
                                        factFind?.adviceFor === 'couple' && factFind?.partner?.dob
                                            ? calculateAge(factFind.partner.dob)
                                            : null
                                    }
                                />
                            ) : (
                                <p className="text-sm text-gray-500">Annuity configuration not initialized.</p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
};

export default RVOtherStrategiesSection;
